import React from 'react';

const platforms = [
  {
    logo: '🟢',
    name: 'OpenAI',
    tagline: 'GPT-4o, Assistants API & More',
    color: 'border-green-400',
    badgeColor: 'bg-green-100 text-green-700',
    services: [
      'GPT-4o fine-tuning & prompt engineering',
      'Assistants API with file retrieval & code interpreter',
      'Embeddings & vector search (RAG pipelines)',
      'Real-time voice and vision integrations',
      'Azure OpenAI Service deployment',
    ],
  },
  {
    logo: '🔵',
    name: 'Anthropic Claude',
    tagline: 'Claude 3.5 & Claude Agent SDK',
    color: 'border-blue-400',
    badgeColor: 'bg-blue-100 text-blue-700',
    services: [
      'Claude Sonnet / Opus / Haiku API integration',
      'Claude Agent SDK for autonomous workflows',
      'Extended context window document processing',
      'Tool use & computer use automation',
      'Safety-first enterprise AI governance',
    ],
  },
];

const useCases = [
  { icon: '📝', title: 'Document Intelligence', desc: 'Automate contract review, invoice processing, and report generation.' },
  { icon: '💬', title: 'Conversational AI', desc: 'Build enterprise chatbots and virtual assistants with long-term memory.' },
  { icon: '🔍', title: 'RAG & Knowledge Bases', desc: 'Connect AI to your internal data with retrieval-augmented generation.' },
  { icon: '🏭', title: 'AI Workflow Automation', desc: 'Replace manual multi-step processes with AI-powered agent pipelines.' },
  { icon: '📊', title: 'AI-Driven Analytics', desc: 'Natural language querying of databases and BI reports.' },
  { icon: '🛡️', title: 'Responsible AI & Governance', desc: 'Bias detection, audit trails, and compliance frameworks for AI systems.' },
];

const AIConsulting = () => {
  return (
    <section id="ai-consulting" className="py-20 bg-gradient-to-br from-slate-900 to-blue-950 text-white">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-14">
          <span className="inline-block bg-white/10 border border-white/20 text-cyan-300 text-sm font-semibold px-3 py-1 rounded-full mb-4">
            🤖 AI Consulting
          </span>
          <h2 className="text-4xl font-bold mb-4">
            AI Strategy & Implementation
          </h2>
          <p className="text-blue-200 text-lg max-w-2xl mx-auto">
            We help enterprises harness the world's most powerful AI platforms — OpenAI and Anthropic Claude — to build intelligent products and automate complex workflows.
          </p>
        </div>

        {/* Platform cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-14">
          {platforms.map((p, i) => (
            <div key={i} className={`bg-white/5 backdrop-blur border-2 ${p.color} rounded-2xl p-7`}>
              <div className="flex items-center gap-3 mb-4">
                <span className="text-4xl">{p.logo}</span>
                <div>
                  <h3 className="text-xl font-bold">{p.name}</h3>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${p.badgeColor}`}>
                    {p.tagline}
                  </span>
                </div>
              </div>
              <ul className="space-y-2">
                {p.services.map((s, j) => (
                  <li key={j} className="flex items-start gap-2 text-blue-100 text-sm">
                    <span className="text-cyan-400 mt-0.5">✔</span>
                    {s}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Use cases */}
        <h3 className="text-2xl font-bold text-center mb-8">Common AI Use Cases We Deliver</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {useCases.map((u, i) => (
            <div key={i} className="bg-white/5 rounded-xl p-5 border border-white/10 hover:border-cyan-400/50 transition-all">
              <div className="text-3xl mb-2">{u.icon}</div>
              <h4 className="font-bold text-lg mb-1">{u.title}</h4>
              <p className="text-blue-200 text-sm leading-relaxed">{u.desc}</p>
            </div>
          ))}
        </div>

        <div className="text-center">
          <a
            href="#contact"
            className="inline-block bg-cyan-400 text-slate-900 px-8 py-3 rounded-full font-bold hover:bg-cyan-300 transition-colors text-lg"
          >
            Start Your AI Journey
          </a>
        </div>
      </div>
    </section>
  );
};

export default AIConsulting;
